import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import GenericButton from "components/Buttons/GenericButton";
import GenericDialog from "components/Dialogs/GenericDialogV2";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import {
  activateCooperativeMember,
  getCooperativeMemberDocument,
  getCooperativeMemberDetails,
  inactivateCooperativeMember,
  updateCooperativeMember,
} from "crud/cooperativeMembers.crud";
import { useDataset } from "hooks/useDataset";
import useMessage from "hooks/useMessage";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { DATASET } from "utils/const";
import CooperativeMemberFormFields from "../CooperativeMemberFormFields";
import CooperativeMemberInactivationDetails from "../CooperativeMemberInactivationDetails";
import CooperativeMemberInactivationModal from "../CooperativeMemberInactivationModal";
import {
  applyCooperativeMemberApiErrors,
  buildInactivationCooperativeMemberFormData,
  buildUpdateCooperativeMemberFormData,
  EMPTY_INACTIVATION_FORM_VALUES,
  getApiErrorMessage,
  getCooperativeMemberFormValues,
  invalidateCooperativeMemberQueries,
  openCooperativeMemberConfirmationDialog,
  resolveCooperativeMemberDocuments,
  showCooperativeMemberSuccessDialog,
} from "../cooperativeMember.utils";

const CooperativeMemberDetailsModal = ({ open, handleClose, memberId }) => {
  const { showErrorMessage } = useMessage();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInactivationModalOpen, setIsInactivationModalOpen] = useState(false);

  const methods = useForm({
    defaultValues: {},
    shouldFocusError: false,
  });

  const inactivationMethods = useForm({
    defaultValues: EMPTY_INACTIVATION_FORM_VALUES,
    shouldFocusError: false,
  });

  const { data: PROFESSIONAL_CATEGORY } = useDataset({ name: DATASET.PROFESSIONAL_CATEGORY });
  const { data: COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES } = useDataset({ name: DATASET.COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES });

  const resetMemberForm = (data) => {
    methods.clearErrors();
    methods.reset(getCooperativeMemberFormValues(data));
  };

  const resetInactivationForm = () => {
    inactivationMethods.clearErrors(["inactivationReasonInput", "inactivationDocument"]);
    inactivationMethods.reset(EMPTY_INACTIVATION_FORM_VALUES);
  };

  const {
    data: memberData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["getCooperativeMemberDetails", memberId],
    queryFn: () => getCooperativeMemberDetails(memberId),
    enabled: !!memberId && open,
    onSuccess: (data) => {
      resetMemberForm(data);
      setIsEditMode(false);
      resetInactivationForm();
      setIsInactivationModalOpen(false);
    },
    onError: () => {
      showErrorMessage("Erro ao carregar detalhes do membro da cooperativa.");
    },
  });

  const cooperativeMemberDocuments = useMemo(() => resolveCooperativeMemberDocuments(memberData), [memberData]);
  const previewDocumentPromise = (documentKey) => getCooperativeMemberDocument(memberId, documentKey);

  const { mutateAsync: updateCooperativeMemberAsync, isLoading: isUpdating } = useMutation(
    (data) => updateCooperativeMember(data),
    {
      onSuccess: () => {
        invalidateCooperativeMemberQueries(queryClient, memberId);
        showCooperativeMemberSuccessDialog(dispatch, "Membro da Cooperativa editado com sucesso.", () => {
          setIsEditMode(false);
          handleClose();
        });
      },
      onError: (error) => {
        const apiErrors = error?.response?.data?.errors;
        const unmappedMessages = applyCooperativeMemberApiErrors(apiErrors, methods.setError);
        const errorMessage =
          unmappedMessages.join(". ") ||
          error?.response?.data?.message ||
          "Ocorreu um erro ao atualizar o Membro da Cooperativa.";

        if (!apiErrors || unmappedMessages.length > 0) {
          showErrorMessage(errorMessage);
        }
      },
    },
  );

  const { mutateAsync: activateMemberAsync, isLoading: isActivating } = useMutation(
    () => activateCooperativeMember(memberId),
    {
      onSuccess: () => {
        invalidateCooperativeMemberQueries(queryClient, memberId);
        showCooperativeMemberSuccessDialog(dispatch, "Membro da Cooperativa activado com sucesso.", handleClose);
      },
      onError: (error) => {
        showErrorMessage(getApiErrorMessage(error, "Ocorreu um erro ao ativar o Membro da Cooperativa."));
      },
    },
  );

  const { mutateAsync: inactivateMemberAsync, isLoading: isInactivating } = useMutation(
    (data) => inactivateCooperativeMember(memberId, data),
    {
      onSuccess: () => {
        invalidateCooperativeMemberQueries(queryClient, memberId);
        showCooperativeMemberSuccessDialog(dispatch, "Membro da Cooperativa inactivado com sucesso.", handleClose);
      },
      onError: (error) => {
        showErrorMessage(getApiErrorMessage(error, "Ocorreu um erro ao inactivar o Membro da Cooperativa."));
      },
    },
  );

  const handleEdit = () => {
    methods.clearErrors();
    setIsInactivationModalOpen(false);
    setIsEditMode(true);
  };

  const handleActivate = () => {
    openCooperativeMemberConfirmationDialog(
      dispatch,
      "Deseja confirmar a activação do Membro da Cooperativa?",
      async () => {
        await activateMemberAsync();
      },
    );
  };

  const handleInactivate = () => {
    resetInactivationForm();
    setIsEditMode(false);
    setIsInactivationModalOpen(true);
  };

  const handleCancel = () => {
    resetMemberForm(memberData);
    setIsEditMode(false);
  };

  const handleCancelInactivation = () => {
    resetInactivationForm();
    setIsInactivationModalOpen(false);
  };

  const handleDialogClose = () => {
    if (isEditMode) {
      resetMemberForm(memberData);
      setIsEditMode(false);
      return;
    }

    handleClose();
  };

  const validateReadonlyRequiredFields = (data) => {
    const requiredReadonlyFields = [
      ["nif", data.nif ?? memberData?.nif],
      ["identityDocumentNumber", data.identityDocumentNumber ?? memberData?.identityDocumentNumber],
    ];

    return requiredReadonlyFields.reduce((hasErrors, [fieldName, fieldValue]) => {
      if (fieldValue) {
        return hasErrors;
      }

      methods.setError(fieldName, {
        type: "required",
        message: "Campo obrigatório.",
      });

      return true;
    }, false);
  };

  const onSubmit = async (data) => {
    methods.clearErrors();

    if (validateReadonlyRequiredFields(data)) {
      return;
    }

    const apiFormData = buildUpdateCooperativeMemberFormData({
      values: data,
      memberId,
      memberData,
      documents: cooperativeMemberDocuments,
    });

    openCooperativeMemberConfirmationDialog(
      dispatch,
      "Deseja confirmar a edição do Membro da Cooperativa?",
      async () => {
        await updateCooperativeMemberAsync(apiFormData);
      },
    );
  };

  const onSubmitInactivation = async (data) => {
    const inactivationData = buildInactivationCooperativeMemberFormData(data);

    openCooperativeMemberConfirmationDialog(
      dispatch,
      "Deseja confirmar a inactivação do Membro da Cooperativa?",
      async () => {
        await inactivateMemberAsync(inactivationData);
      },
    );
  };

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && open && !isEditMode && !isInactivationModalOpen) {
        handleClose();
      }
    };

    if (open) {
      window.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      window.removeEventListener("keydown", handleEscapeKey);
    };
  }, [open, isEditMode, isInactivationModalOpen, handleClose]);

  const isInactive = memberData && !memberData.isActive;
  const areEditableFieldsDisabled = isInactive || !isEditMode;
  const disabledFields = {
    name: areEditableFieldsDisabled,
    functionId: areEditableFieldsDisabled,
    dateOfBirth: areEditableFieldsDisabled,
    nif: true,
    bankAccountNumber: areEditableFieldsDisabled,
    documentTypeId: areEditableFieldsDisabled,
    identityDocumentNumber: true,
    identityDocumentIssueDate: areEditableFieldsDisabled,
    identityDocumentExpiryDate: areEditableFieldsDisabled,
  };
  const existingDocuments = [
    {
      key: "identityDocument",
      label: "Documento de identificação",
      document: cooperativeMemberDocuments.identityDocument,
    },
    {
      key: "entrepreneurshipCertificateDocument",
      label: "Certificado de Empreendedorismo",
      document: cooperativeMemberDocuments.entrepreneurshipCertificateDocument,
    },
  ];

  return (
    <>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <GenericDialog open={open} onClose={handleDialogClose} maxWidth="lg">
          <Box display="flex" justifyContent="start" alignItems="center" mb={2}>
            <PageTitleHeader title="Membro da Cooperativa" style={{ marginBottom: "0" }} />
            <Box display="flex" style={{ gap: "10px" }}>
              {isInactive ? (
                <GenericButton
                  size="small"
                  color="primary"
                  onClick={handleActivate}
                  disabled={isLoading || isActivating}
                  variant="outlined"
                >
                  ACTIVAR MEMBRO DA COOPERATIVA
                </GenericButton>
              ) : !isEditMode ? (
                <>
                  <GenericButton
                    size="small"
                    color="primary"
                    onClick={handleEdit}
                    disabled={isLoading}
                    variant="outlined"
                  >
                    EDITAR
                  </GenericButton>
                  <GenericButton
                    size="small"
                    color="tertiaryRed"
                    onClick={handleInactivate}
                    disabled={isLoading || isInactivating}
                    variant="outlined"
                  >
                    INACTIVAR MEMBRO DA COOPERATIVA
                  </GenericButton>
                </>
              ) : null}
            </Box>
          </Box>

          {isInactive && (
            <CooperativeMemberInactivationDetails
              memberData={memberData}
              inactivationDocument={cooperativeMemberDocuments.inactivationDocument}
              previewDocumentPromise={previewDocumentPromise}
            />
          )}
          {!isInactive && (
            <Typography variant="body2" style={{ color: "#666", marginBottom: "1.5rem" }}>
              Ao adicionar um novo registo de membro, o sistema irá actualizar na sua área de utilizador a lista de
              membros registados da sua cooperativa.
            </Typography>
          )}
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <Typography color="error">Erro ao carregar os detalhes do membro.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <CooperativeMemberFormFields
                methods={methods}
                professionalCategoryOptions={PROFESSIONAL_CATEGORY}
                documentTypeOptions={COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES}
                disabledFields={disabledFields}
                existingDocuments={existingDocuments}
                previewDocumentPromise={previewDocumentPromise}
                showUploadSection={isEditMode && !isInactive}
              />

              <Grid
                item
                xs={12}
                md={12}
                style={{
                  gap: "1rem",
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "2rem",
                }}
              >
                {!isEditMode && (
                  <GenericButton typeSubmit={false} size="medium" color="tertiary" onClick={handleClose}>
                    Fechar
                  </GenericButton>
                )}
                {isEditMode && (
                  <>
                    <GenericButton
                      typeSubmit={false}
                      size="medium"
                      color="tertiary"
                      onClick={handleCancel}
                      disabled={isUpdating}
                    >
                      CANCELAR
                    </GenericButton>
                    <GenericButton
                      typeSubmit={false}
                      size="medium"
                      color="primary"
                      onClick={methods.handleSubmit(onSubmit)}
                      disabled={isUpdating}
                      loading={isUpdating}
                    >
                      GUARDAR ALTERAÇÕES
                    </GenericButton>
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </GenericDialog>
      </form>

      <form onSubmit={inactivationMethods.handleSubmit(onSubmitInactivation)}>
        <CooperativeMemberInactivationModal
          methods={inactivationMethods}
          open={isInactivationModalOpen}
          onClose={handleCancelInactivation}
          onSubmit={onSubmitInactivation}
          isSubmitting={isInactivating}
        />
      </form>
    </>
  );
};

export default CooperativeMemberDetailsModal;
